import React from 'react';
import classNames from 'classnames';

import css from './OccasionIcon.module.css';

/**
 * Bow tie icon for Black Tie / Formal occasions
 */
const IconBlackTie = ({ className }) => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 16 16"
    fill="currentColor"
    className={className}
    role="presentation"
  >
    <path d="M1.5 4.5L7 7.5V8.5L1.5 11.5C1.1 11.7 0.75 11.4 0.75 11V5C0.75 4.6 1.1 4.3 1.5 4.5Z" />
    <path d="M14.5 4.5L9 7.5V8.5L14.5 11.5C14.9 11.7 15.25 11.4 15.25 11V5C15.25 4.6 14.9 4.3 14.5 4.5Z" />
    <rect x="6.75" y="6" width="2.5" height="4" rx="0.8" />
  </svg>
);

/**
 * Champagne / cocktail coupe flute for Brunch
 */
const IconBrunch = ({ className }) => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.35"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    role="presentation"
  >
    <path d="M3.2 3.5H12.8L8 9L3.2 3.5Z" />
    <line x1="8" y1="9" x2="8" y2="13.5" />
    <line x1="5.5" y1="13.5" x2="10.5" y2="13.5" />
    <circle cx="8" cy="5.5" r="0.7" fill="currentColor" stroke="none" />
  </svg>
);

/**
 * Interlocking rings with diamond sparkle for Wedding Guest
 */
const IconWeddingGuest = ({ className }) => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.3"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    role="presentation"
  >
    <circle cx="6" cy="9.5" r="3.6" />
    <circle cx="10" cy="7.5" r="3.6" />
    <path
      d="M10 2L10.5 3.3L11.8 3.8L10.5 4.3L10 5.6L9.5 4.3L8.2 3.8L9.5 3.3L10 2Z"
      fill="currentColor"
      stroke="none"
    />
  </svg>
);

/**
 * Heart icon for Date Night
 */
const IconDateNight = ({ className }) => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 16 16"
    fill="currentColor"
    className={className}
    role="presentation"
  >
    <path d="M8 14.2L6.85 13.15C2.8 9.47 1 7.75 1 5.3C1 3.32 2.52 1.8 4.5 1.8C5.62 1.8 6.7 2.32 7.4 3.14H8.6C9.3 2.32 10.38 1.8 11.5 1.8C13.48 1.8 15 3.32 15 5.3C15 7.75 13.2 9.47 9.15 13.15L8 14.2Z" />
  </svg>
);

/**
 * Sun with rays for Vacation / Resort
 */
const IconVacation = ({ className }) => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.3"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    role="presentation"
  >
    <circle cx="8" cy="8" r="3" />
    <line x1="8" y1="2" x2="8" y2="3.5" />
    <line x1="8" y1="12.5" x2="8" y2="14" />
    <line x1="2" y1="8" x2="3.5" y2="8" />
    <line x1="12.5" y1="8" x2="14" y2="8" />
    <line x1="3.75" y1="3.75" x2="4.8" y2="4.8" />
    <line x1="11.2" y1="11.2" x2="12.25" y2="12.25" />
    <line x1="3.75" y1="12.25" x2="4.8" y2="11.2" />
    <line x1="11.2" y1="4.8" x2="12.25" y2="3.75" />
  </svg>
);

/**
 * Briefcase icon for Office / Work
 */
const IconOffice = ({ className }) => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.3"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    role="presentation"
  >
    <rect x="2.5" y="5.5" width="11" height="8" rx="1.5" />
    <path d="M5.5 5.5V4C5.5 3.17 6.17 2.5 7 2.5H9C9.83 2.5 10.5 3.17 10.5 4V5.5" />
    <line x1="2.5" y1="9.5" x2="13.5" y2="9.5" />
  </svg>
);

/**
 * Luxury 4-point sparkle star for generic / fallback occasion
 */
const IconSparkle = ({ className }) => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 16 16"
    fill="currentColor"
    className={className}
    role="presentation"
  >
    <path d="M8 1C8 4.87 4.87 8 1 8C4.87 8 8 11.13 8 15C8 11.13 11.13 8 15 8C11.13 8 8 4.87 8 1Z" />
  </svg>
);

const normalize = str =>
  (str || '')
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-');

const getIconComponent = (optionKey, label) => {
  const normKey = normalize(optionKey);
  const normLabel = normalize(label);
  const combined = `${normKey}-${normLabel}`;

  if (combined.includes('black-tie') || combined.includes('gala') || combined.includes('formal')) {
    return IconBlackTie;
  }
  if (combined.includes('brunch') || combined.includes('cocktail') || combined.includes('party')) {
    return IconBrunch;
  }
  if (combined.includes('wedding')) {
    return IconWeddingGuest;
  }
  if (combined.includes('date')) {
    return IconDateNight;
  }
  if (combined.includes('vacation') || combined.includes('holiday') || combined.includes('resort')) {
    return IconVacation;
  }
  if (combined.includes('office') || combined.includes('work') || combined.includes('business')) {
    return IconOffice;
  }
  return IconSparkle;
};

/**
 * Occasion icon rendered in place of default checkmarks for occasion items.
 * Maps known fashion occasions to dedicated luxury icons, with an elegant sparkle fallback.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.optionKey] - The option key (e.g. 'black-tie', 'brunch')
 * @param {string} [props.label] - The display label (e.g. 'Black Tie')
 * @param {boolean} [props.isVisible=true] - Whether the icon should be visible
 * @param {string} [props.className] - Additional custom CSS class
 * @returns {JSX.Element}
 */
const OccasionIcon = props => {
  const { optionKey, label, isVisible = true, className, rootClassName } = props;
  const Component = getIconComponent(optionKey, label);
  const classes = classNames(
    rootClassName || css.root,
    { [css.hidden]: !isVisible },
    className
  );

  return (
    <span className={classes} aria-hidden="true">
      <Component className={css.icon} />
    </span>
  );
};

export default OccasionIcon;
